/**
 * CreateFormModal.tsx — Módulo Formularios. Extraído de FormsPage.tsx (mismo
 * criterio que CreateProjectModal). Dos modos:
 * - Manual: nombre + descripción, como antes.
 * - Importar: Eduardo pega un formulario existente (HTML, texto plano, o una
 *   descripción) y Evo Brain lo interpreta en campos — se muestran ANTES de
 *   crear, editables con el mismo FormFieldEditor del constructor (así se
 *   pueden corregir campos mal interpretados sin inventar UI nueva).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { Modal } from '../ui/Modal.js';
import { Tabs } from '../ui/Tabs.js';
import { SegmentedControl } from '../ui/SegmentedControl.js';
import { Icon } from '../Icon.js';
import { useCreateForm, useSaveFormFields, useImportFormParse, type FieldInput, type ImportFormInput } from '../../hooks/useForms.js';
import { FormFieldEditor } from './FormFieldEditor.js';

type Mode = 'manual' | 'import';
type ImportSource = 'paste' | 'url';

export function CreateFormModal({ onClose, onCreated, contact }: {
  onClose: () => void;
  onCreated: (id: string) => void;
  /** Cliente dueño precargado — viene del + de la tarjeta Formularios de un contacto. */
  contact?: { id: number; name: string } | null;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('manual');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [importSource, setImportSource] = useState<ImportSource>('paste');
  const [pasteContent, setPasteContent] = useState('');
  const [importUrl, setImportUrl] = useState('');
  const [importedFields, setImportedFields] = useState<FieldInput[] | null>(null);
  const [missingOptions, setMissingOptions] = useState<string[]>([]);
  // Mensaje largo y accionable (ej. "pegaste un iframe"): no entra en un toast.
  const [importNotice, setImportNotice] = useState<string | null>(null);

  const create = useCreateForm();
  const saveFields = useSaveFormFields();
  const importParse = useImportFormParse();

  function handleCreateManual() {
    if (!name.trim()) return;
    create.mutate({ name, ...(description ? { description } : {}), ...(contact ? { contactId: contact.id } : {}) }, {
      onSuccess: form => onCreated(form.id),
    });
  }

  function handleAnalyze() {
    const input: ImportFormInput | null = importSource === 'url'
      ? (importUrl.trim() ? { url: importUrl.trim() } : null)
      : (pasteContent.trim() ? { content: pasteContent } : null);
    if (!input) return;

    setImportNotice(null);
    importParse.mutate(input, {
      onSuccess: result => {
        if (result.fields.length === 0) { setImportNotice(t('forms.createModal.importEmpty')); return; }
        setImportedFields(result.fields);
        setMissingOptions(result.fieldsMissingOptions ?? []);
        if (result.name && !name.trim()) setName(result.name);
        if (result.description && !description.trim()) setDescription(result.description);
      },
      onError: err => {
        const raw = err instanceof Error ? err.message : String(err);
        // 'iframe_only' es el caso de Go High Level: el código de incrustar no
        // trae los campos. Se explica en pantalla qué pegar en su lugar, en vez
        // de un "no encontré campos" que no dice cómo seguir.
        if (raw.startsWith('iframe_only')) { setImportNotice(t('forms.createModal.importIframeOnly')); return; }
        if (raw.startsWith('url_')) { setImportNotice(t(`forms.createModal.importUrlError.${raw.slice(4)}`, { defaultValue: t('forms.createModal.importUrlError.fetch_failed') })); return; }
        setImportNotice(raw === 'no_fields_detected' ? t('forms.createModal.importEmpty') : t('forms.createModal.importError'));
      },
    });
  }

  function handleCreateFromImport() {
    if (!name.trim() || !importedFields) return;
    create.mutate({ name, ...(description ? { description } : {}), ...(contact ? { contactId: contact.id } : {}) }, {
      onSuccess: form => {
        saveFields.mutate({ id: form.id, fields: importedFields }, {
          onSuccess: () => onCreated(form.id),
        });
      },
    });
  }

  const busy = create.isPending || saveFields.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('forms.createModal.title')}
      size={importedFields ? 'lg' : 'sm'}
      footer={
        mode === 'manual' ? (
          <>
            <Button variant="outline" tone="muted" size="sm" onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="solid" tone="cyan" size="sm" loading={create.isPending} disabled={!name.trim()} onClick={handleCreateManual}>
              {t('forms.createModal.createBtn')}
            </Button>
          </>
        ) : importedFields ? (
          <>
            <Button variant="outline" tone="muted" size="sm" onClick={() => setImportedFields(null)}>{t('forms.createModal.importBack')}</Button>
            <Button variant="solid" tone="cyan" size="sm" loading={busy} disabled={!name.trim()} onClick={handleCreateFromImport}>
              {t('forms.createModal.createFromImportBtn')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" tone="muted" size="sm" onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="solid" tone="cyan" size="sm" loading={importParse.isPending} disabled={importSource === 'url' ? !importUrl.trim() : !pasteContent.trim()} onClick={handleAnalyze}>
              {t('forms.createModal.importBtn')}
            </Button>
          </>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
        <Tabs<Mode>
          value={mode}
          onChange={m => { setMode(m); setImportedFields(null); }}
          size="sm"
          tabs={[
            { value: 'manual', label: t('forms.createModal.modeManual') },
            { value: 'import', label: t('forms.createModal.modeImport') },
          ]}
        />

        {mode === 'manual' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
            <div>
              <div style={{ marginBottom: '6px', fontSize: '12px', color: 'var(--evo-fg-3)' }}>{t('forms.createModal.nameLabel')}</div>
              <Input type="text" size="sm" fullWidth value={name} onChange={setName} placeholder={t('forms.createModal.namePlaceholder')} autoFocus />
            </div>
            <div>
              <div style={{ marginBottom: '6px', fontSize: '12px', color: 'var(--evo-fg-3)' }}>{t('forms.createModal.descriptionLabel')}</div>
              <Textarea fullWidth value={description} onChange={setDescription} rows={3} placeholder={t('forms.createModal.descriptionPlaceholder')} />
            </div>
          </div>
        )}

        {mode === 'import' && !importedFields && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
            <SegmentedControl<ImportSource>
              value={importSource}
              onChange={s => { setImportSource(s); setImportNotice(null); }}
              options={[
                { value: 'paste', label: t('forms.createModal.sourcePaste') },
                { value: 'url', label: t('forms.createModal.sourceUrl') },
              ]}
              size="sm"
            />

            {importSource === 'paste' ? (
              <Textarea
                fullWidth
                value={pasteContent}
                onChange={v => { setPasteContent(v); setImportNotice(null); }}
                rows={10}
                placeholder={t('forms.createModal.importPlaceholder')}
                autoFocus
              />
            ) : (
              <div>
                <Input
                  type="url"
                  size="sm"
                  fullWidth
                  value={importUrl}
                  onChange={v => { setImportUrl(v); setImportNotice(null); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAnalyze(); } }}
                  placeholder={t('forms.createModal.importUrlPlaceholder')}
                  autoFocus
                />
                <p style={{ marginTop: '6px', fontSize: '12px', color: 'var(--evo-fg-3)' }}>
                  {t('forms.createModal.importUrlHint')}
                </p>
              </div>
            )}

            {importNotice && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-2)' }}>
                <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-amber)', marginTop: '1px' }}>
                  <Icon name="alert" size={13} />
                </span>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', lineHeight: 1.5 }}>
                  {importNotice}
                </span>
              </div>
            )}
          </div>
        )}

        {mode === 'import' && importedFields && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
              <div>
                <div style={{ marginBottom: '6px', fontSize: '12px', color: 'var(--evo-fg-3)' }}>{t('forms.createModal.nameLabel')}</div>
                <Input type="text" size="sm" fullWidth value={name} onChange={setName} placeholder={t('forms.createModal.namePlaceholder')} />
              </div>
              <div>
                <div style={{ marginBottom: '6px', fontSize: '12px', color: 'var(--evo-fg-3)' }}>{t('forms.createModal.descriptionLabel')}</div>
                <Textarea fullWidth value={description} onChange={setDescription} rows={2} placeholder={t('forms.createModal.descriptionPlaceholder')} />
              </div>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: 'var(--evo-fg-3)', marginBottom: 'var(--evo-space-3)' }}>
                {t('forms.createModal.importReviewHint')}
              </p>

              {/* Campos de lista que llegaron sin opciones. Se avisan por
                  nombre para completarlas a mano — nunca se inventan. */}
              {missingOptions.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-2)', marginBottom: 'var(--evo-space-3)' }}>
                  <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-amber)', marginTop: '1px' }}>
                    <Icon name="alert" size={13} />
                  </span>
                  <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', lineHeight: 1.5 }}>
                    {t('forms.createModal.importMissingOptions', { count: missingOptions.length, fields: missingOptions.join(', ') })}
                  </span>
                </div>
              )}

              <FormFieldEditor fields={importedFields} onChange={setImportedFields} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
